import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import Header from '../../components/navigation/Header';
import ComplianceAlertBanner from '../../components/navigation/ComplianceAlertBanner';
import DataRefreshIndicator from '../../components/navigation/DataRefreshIndicator';
import Button from '../../components/ui/Button';
import Select from '../../components/ui/Select';
import Icon from '../../components/AppIcon';
import complianceReportService from '../../services/complianceReportService';
import VarianceAnalysisTable from '../compliance-library/components/VarianceAnalysisTable';
import AuditTrailTimeline from '../compliance-library/components/AuditTrailTimeline';
import DlirSubmissionPackCard from '../compliance-library/components/DlirSubmissionPackCard';
import DepartmentBreakdownTable from '../compliance-library/components/DepartmentBreakdownTable';
import KeyFindingsCard from '../compliance-library/components/KeyFindingsCard';
import * as XLSX from 'xlsx';
import Papa from 'papaparse';

const ComplianceReportDetails = () => {
  const { reportId } = useParams();
  const navigate = useNavigate();
  const [report, setReport] = useState(null);
  const [varianceData, setVarianceData] = useState([]);
  const [auditTrail, setAuditTrail] = useState([]);
  const [dlirPacks, setDlirPacks] = useState([]);
  const [departmentBreakdown, setDepartmentBreakdown] = useState([]);
  const [keyFindings, setKeyFindings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [activeTab, setActiveTab] = useState('overview');
  const [exportFormat, setExportFormat] = useState('pdf');
  const [isExporting, setIsExporting] = useState(false);

  useEffect(() => {
    loadReportData();
  }, [reportId]);

  const loadReportData = async () => {
    try {
      setLoading(true);
      setError('');

      const [reportData, variance, audit, packs, breakdown, findings] = await Promise.all([
        complianceReportService?.getReportById(reportId),
        complianceReportService?.getVarianceAnalysis(reportId),
        complianceReportService?.getReportAuditTrail(reportId),
        complianceReportService?.getDlirSubmissionPacks(reportId),
        complianceReportService?.getDepartmentBreakdown(reportId),
        complianceReportService?.getKeyFindings(reportId)
      ]);

      setReport(reportData);
      setVarianceData(variance);
      setAuditTrail(audit);
      setDlirPacks(packs);
      setDepartmentBreakdown(breakdown);
      setKeyFindings(findings);
    } catch (err) {
      setError(err?.message || 'Failed to load report data');
    } finally {
      setLoading(false);
    }
  };

  const handleExportReport = async () => {
    try {
      setIsExporting(true);

      if (exportFormat === 'excel') {
        exportToExcel();
      } else if (exportFormat === 'csv') {
        exportToCSV();
      } else {
        exportToPDF();
      }
    } catch (err) {
      setError(err?.message || 'Export failed');
    } finally {
      setIsExporting(false);
    }
  };

  const exportToExcel = () => {
    const wb = XLSX?.utils?.book_new();

    // Summary sheet
    const summaryData = [
      ['Compliance Report Summary'],
      ['Report Name', report?.reportName],
      ['Period', `${report?.periodStart} to ${report?.periodEnd}`],
      ['Status', report?.reportStatus],
      ['Overall Score', `${report?.overallComplianceScore}%`],
      ['Variance', `${report?.variancePercentage}%`],
      ['Total Violations', report?.totalViolations],
      ['Critical Violations', report?.criticalViolations],
      ['Total Employees', report?.totalEmployees],
      ['Compliant Employees', report?.compliantEmployees]
    ];
    const summarySheet = XLSX?.utils?.aoa_to_sheet(summaryData);
    XLSX?.utils?.book_append_sheet(wb, summarySheet, 'Summary');

    // Variance Analysis sheet
    if (varianceData?.length > 0) {
      const varianceSheet = XLSX?.utils?.json_to_sheet(
        varianceData?.map(v => ({
          'Metric': v?.metricName,
          'Category': v?.metricCategory,
          'Current Value': v?.currentValue,
          'Previous Value': v?.previousValue,
          'Variance %': v?.variancePercentage,
          'Type': v?.varianceType,
          'Department': v?.department,
          'Notes': v?.analysisNotes
        }))
      );
      XLSX?.utils?.book_append_sheet(wb, varianceSheet, 'Variance Analysis');
    }

    // Department Breakdown sheet
    if (departmentBreakdown?.length > 0) {
      const deptSheet = XLSX?.utils?.json_to_sheet(
        departmentBreakdown?.map(d => ({
          'Department': d?.department,
          'Compliance Score': d?.complianceScore,
          'Previous Score': d?.previousScore,
          'Variance %': d?.variancePercentage,
          'Employees': d?.totalEmployees,
          'Violations': d?.violationsCount,
          'Critical': d?.criticalViolations,
          'Payroll Accuracy': d?.payrollAccuracy,
          'Leave Compliance': d?.leaveCompliance,
          'Contract Compliance': d?.contractCompliance
        }))
      );
      XLSX?.utils?.book_append_sheet(wb, deptSheet, 'Department Breakdown');
    }

    // Key Findings sheet
    if (keyFindings?.length > 0) {
      const findingsSheet = XLSX?.utils?.json_to_sheet(
        keyFindings?.map(f => ({
          'Type': f?.findingType,
          'Severity': f?.severity,
          'Title': f?.title,
          'Description': f?.description,
          'Departments': f?.affectedDepartments?.join(', '),
          'Affected Employees': f?.affectedEmployeeCount,
          'Recommendation': f?.recommendation,
          'Action Required': f?.actionRequired ? 'Yes' : 'No',
          'Deadline': f?.deadline
        }))
      );
      XLSX?.utils?.book_append_sheet(wb, findingsSheet, 'Key Findings');
    }

    XLSX?.writeFile(wb, `${report?.reportName}_${new Date()?.toISOString()?.split('T')?.[0]}.xlsx`);
  };

  const exportToCSV = () => {
    const csvData = varianceData?.map(v => ({
      'Metric': v?.metricName,
      'Category': v?.metricCategory,
      'Current Value': v?.currentValue,
      'Previous Value': v?.previousValue,
      'Variance %': v?.variancePercentage,
      'Type': v?.varianceType,
      'Department': v?.department,
      'Notes': v?.analysisNotes
    }));

    const csv = Papa?.unparse(csvData);
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    link.href = URL.createObjectURL(blob);
    link.download = `${report?.reportName}_variance_analysis_${new Date()?.toISOString()?.split('T')?.[0]}.csv`;
    link?.click();
  };

  const exportToPDF = () => {
    alert('PDF export functionality would use jsPDF + html2canvas to generate board-ready PDF report');
  };

  const handleUpdateDlirStatus = async (packId, newStatus) => {
    try {
      await complianceReportService?.updateDlirSubmissionStatus(packId, newStatus);
      await loadReportData();
    } catch (err) {
      setError(err?.message || 'Failed to update DLIR submission status');
    }
  };

  const handleDownloadDlirPack = (packId) => {
    alert(`Downloading DLIR submission pack ${packId}`);
  };

  const getVarianceColor = (type) => {
    if (type === 'positive') return 'text-success';
    if (type === 'negative') return 'text-error';
    return 'text-muted-foreground';
  };

  const getVarianceIcon = (type) => {
    if (type === 'positive') return 'TrendingUp';
    if (type === 'negative') return 'TrendingDown';
    return 'Minus';
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString)?.toLocaleDateString('en-PG', { 
      year: 'numeric', 
      month: 'short', 
      day: 'numeric' 
    });
  };

  const exportFormatOptions = [
    { value: 'pdf', label: 'PDF Report' },
    { value: 'excel', label: 'Excel Spreadsheet' },
    { value: 'csv', label: 'CSV Data' }
  ];

  const tabs = [
    { id: 'overview', label: 'Overview', icon: 'LayoutDashboard' },
    { id: 'variance', label: 'Variance Analysis', icon: 'TrendingUp' },
    { id: 'departments', label: 'Departments', icon: 'Building2' },
    { id: 'findings', label: 'Key Findings', icon: 'Search' },
    { id: 'dlir', label: 'DLIR Submissions', icon: 'Package' },
    { id: 'audit', label: 'Audit Trail', icon: 'History' }
  ];

  if (loading) {
    return (
      <div className="min-h-screen bg-background">
        <Header />
        <ComplianceAlertBanner />
        <DataRefreshIndicator />
        <main className="pt-20 pb-12 px-4 md:px-6 lg:px-8 max-w-7xl mx-auto">
          <div className="flex items-center justify-center py-12">
            <Icon name="Loader2" size={48} color="var(--color-primary)" className="animate-spin" />
          </div>
        </main>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-background">
        <Header />
        <ComplianceAlertBanner />
        <DataRefreshIndicator />
        <main className="pt-20 pb-12 px-4 md:px-6 lg:px-8 max-w-7xl mx-auto">
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-6 text-center">
            <Icon name="AlertCircle" size={48} color="var(--color-primary)" className="mx-auto mb-3" />
            <p className="text-blue-800 font-medium">{error}</p>
            <Button variant="outline" onClick={() => navigate('/compliance-library')} className="mt-4">
              Back to Compliance Library
            </Button>
          </div>
        </main>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      <Header />
      <ComplianceAlertBanner />
      <DataRefreshIndicator />
      <main className="pt-20 pb-12 px-4 md:px-6 lg:px-8 max-w-7xl mx-auto">
        <div className="mb-6">
          <Button
            variant="ghost"
            size="sm"
            onClick={() => navigate('/compliance-library')}
            iconName="ArrowLeft"
            iconPosition="left"
            className="mb-4"
          >
            Back to Compliance Library
          </Button>

          <div className="flex flex-col md:flex-row md:items-start md:justify-between gap-4 mb-4">
            <div>
              <h1 className="text-2xl md:text-3xl lg:text-4xl font-heading font-bold text-foreground mb-2">
                {report?.reportName}
              </h1>
              <p className="caption text-sm md:text-base text-muted-foreground">
                {formatDate(report?.periodStart)} - {formatDate(report?.periodEnd)}
              </p>
            </div>

            <div className="flex items-center gap-2">
              <Select
                options={exportFormatOptions}
                value={exportFormat}
                onChange={setExportFormat}
                className="w-40"
              />
              <Button
                variant="default"
                onClick={handleExportReport}
                loading={isExporting}
                iconName="Download"
                iconPosition="left"
              >
                Export
              </Button>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <div className="bg-card border border-border rounded-lg p-4">
              <p className="caption text-xs text-muted-foreground mb-1">Overall Score</p>
              <p className="font-heading font-bold text-2xl text-foreground">
                {report?.overallComplianceScore?.toFixed(1)}%
              </p>
              <p className="caption text-xs text-muted-foreground mt-1">
                Previous: {report?.previousPeriodScore?.toFixed(1)}%
              </p>
            </div>

            <div className="bg-card border border-border rounded-lg p-4">
              <p className="caption text-xs text-muted-foreground mb-1">Variance</p>
              <div className="flex items-center gap-2">
                <Icon 
                  name={getVarianceIcon(report?.varianceType)} 
                  size={20} 
                  color={getVarianceColor(report?.varianceType)?.replace('text-', 'var(--color-)')} 
                />
                <p className={`font-heading font-bold text-2xl ${getVarianceColor(report?.varianceType)}`}>
                  {report?.variancePercentage > 0 ? '+' : ''}{report?.variancePercentage?.toFixed(2)}%
                </p>
              </div>
            </div>

            <div className="bg-card border border-border rounded-lg p-4">
              <p className="caption text-xs text-muted-foreground mb-1">Violations</p>
              <p className="font-heading font-bold text-2xl text-foreground">
                {report?.totalViolations}
              </p>
              <p className="caption text-xs text-error mt-1">
                {report?.criticalViolations} critical
              </p>
            </div>

            <div className="bg-card border border-border rounded-lg p-4">
              <p className="caption text-xs text-muted-foreground mb-1">Compliance Rate</p>
              <p className="font-heading font-bold text-2xl text-foreground">
                {report?.compliantEmployees}/{report?.totalEmployees}
              </p>
              <p className="caption text-xs text-muted-foreground mt-1">
                {((report?.compliantEmployees / report?.totalEmployees) * 100)?.toFixed(1)}% compliant
              </p>
            </div>
          </div>
        </div>

        <div className="mb-6">
          <div className="flex items-center gap-2 overflow-x-auto pb-2">
            {tabs?.map(tab => (
              <button
                key={tab?.id}
                onClick={() => setActiveTab(tab?.id)}
                className={`flex items-center gap-2 px-4 py-2 rounded-lg font-caption text-sm transition-smooth whitespace-nowrap ${
                  activeTab === tab?.id
                    ? 'bg-primary text-white' :'bg-card border border-border text-foreground hover:bg-muted'
                }`}
              >
                <Icon name={tab?.icon} size={16} />
                {tab?.label}
              </button>
            ))}
          </div>
        </div>

        <div>
          {activeTab === 'overview' && (
            <div className="space-y-6">
              {report?.reportSummary && (
                <div className="bg-card border border-border rounded-lg p-6">
                  <h3 className="font-heading font-semibold text-lg text-foreground mb-3">Executive Summary</h3>
                  <p className="caption text-sm text-muted-foreground leading-relaxed">{report?.reportSummary}</p>
                </div>
              )}
              <DepartmentBreakdownTable departmentData={departmentBreakdown} />
              <KeyFindingsCard findings={keyFindings} />
            </div>
          )}

          {activeTab === 'variance' && (
            <VarianceAnalysisTable varianceData={varianceData} />
          )}

          {activeTab === 'departments' && (
            <DepartmentBreakdownTable departmentData={departmentBreakdown} />
          )}

          {activeTab === 'findings' && (
            <KeyFindingsCard findings={keyFindings} />
          )}

          {activeTab === 'dlir' && (
            <div className="space-y-4">
              {dlirPacks?.map(pack => (
                <DlirSubmissionPackCard
                  key={pack?.id}
                  pack={pack}
                  onUpdateStatus={handleUpdateDlirStatus}
                  onDownload={handleDownloadDlirPack}
                />
              ))}
              {(!dlirPacks || dlirPacks?.length === 0) && (
                <div className="bg-card border border-border rounded-lg p-12 text-center">
                  <Icon name="Package" size={48} color="var(--color-muted-foreground)" className="mx-auto mb-3 opacity-50" />
                  <p className="caption text-sm text-muted-foreground">No DLIR submission packs available</p>
                </div>
              )}
            </div>
          )}

          {activeTab === 'audit' && (
            <AuditTrailTimeline auditTrail={auditTrail} />
          )}
        </div>
      </main>
    </div>
  );
};

export default ComplianceReportDetails;